/**
 * Document processing — upload a document, watch it being read, classified and
 * extracted.
 *
 * The backend answers `/process` as a Server-Sent Events stream, so progress
 * arrives per stage instead of everything landing at the end. EventSource
 * cannot POST a file, so this reads the stream with fetch + ReadableStream,
 * which also lets it carry the Authorization header the rest of the app uses.
 */
import type {
  DocumentLanguage,
  DocumentProcessingEvent,
  DocumentProcessingResult,
} from '../types/document-intelligence.types';

const BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/v1/document-processing`;

export interface ProcessOptions {
  /** Document-analysis (OCR) model id. Omit for the project's default. */
  ocrModelRef?: string | null;
  /** Completion model id. Omit for the project's default. */
  llmModelRef?: string | null;
  /** The document's language. Omit for English. */
  language?: DocumentLanguage;
  /** Called for every progress event, in order. */
  onEvent: (event: DocumentProcessingEvent) => void;
  /** Lets the caller stop the run (navigating away, pressing cancel). */
  signal?: AbortSignal;
}

/** Turn one SSE `data:` block into an event. */
const parseBlock = (block: string): DocumentProcessingEvent | null => {
  const data = block
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .join('');
  if (!data) return null;
  try {
    return JSON.parse(data) as DocumentProcessingEvent;
  } catch {
    return null;
  }
};

export const documentProcessingService = {
  /**
   * Run the pipeline. Resolves with the finished result, or rejects with the
   * reason the stream reported.
   *
   * A stage failure arrives as an event rather than an HTTP status — by then
   * the response has already started — so it is turned back into a rejection
   * here, and the caller sees one failure path.
   */
  process: async (file: File, options: ProcessOptions): Promise<DocumentProcessingResult> => {
    const form = new FormData();
    form.append('file', file);
    if (options.ocrModelRef) form.append('ocr_model_ref', options.ocrModelRef);
    if (options.llmModelRef) form.append('llm_model_ref', options.llmModelRef);
    if (options.language) form.append('language', options.language);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${BASE}/process`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
      signal: options.signal,
    });

    if (!response.ok || !response.body) {
      // Refused before the stream started: a normal JSON error envelope.
      let message = `The server refused the upload (${response.status}).`;
      try {
        const body = await response.json();
        message = body?.message || body?.errors?.details || message;
      } catch { /* not JSON — keep the status message */ }
      throw new Error(message);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: DocumentProcessingResult | null = null;
    let failure: string | null = null;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Events are separated by a blank line; the last piece may be partial.
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';
      for (const block of blocks) {
        const event = parseBlock(block);
        if (!event) continue;
        options.onEvent(event);
        if (event.status === 'failed') failure = event.error || 'Processing failed.';
        if (event.step === 'done' && event.result) result = event.result;
      }
    }

    if (failure) throw new Error(failure);
    if (!result) {
      // The stream ended without a result and without saying why — a dropped
      // connection. Said plainly rather than left as a spinner.
      throw new Error('The connection ended before processing finished.');
    }
    return result;
  },
};
